/**
 * Role-Based Access Control (RBAC) permissions config.
 *
 * Menu visibility:
 *   visibleMenuIds – list of sidebar section ids the role can see.
 *   '*' means all sections.
 *
 * Actions:
 *   canAdd / canEdit / canDelete – global defaults per role.
 *   overrides – per-section overrides (e.g. inventory view-only).
 *
 * Special flags:
 *   hideInventoryPrices – hides purchase_rate & sale_rate columns.
 *   servicesHubTicketsOnly – limits Services Hub to Tickets page only.
 */

const ROLE_PERMISSIONS = {
  Superadmin: {
    visibleMenuIds: '*',
    canAdd: true,
    canEdit: true,
    canDelete: true,
    overrides: {},
  },
  Admin: {
    visibleMenuIds: '*',
    canAdd: true,
    canEdit: false,
    canDelete: false,
    overrides: {},
  },
  Accounts: {
    visibleMenuIds: '*',
    canAdd: true,
    canEdit: false,
    canDelete: false,
    overrides: {},
  },
  'Inventory Manager': {
    visibleMenuIds: ['dashboard', 'inward', 'inventory', 'part-sale', 'part-sale-tracker', 'purchase', 'sale', 'transactions'],
    canAdd: true,
    canEdit: false,
    canDelete: false,
    overrides: {},
  },
  'Sales Manager': {
    visibleMenuIds: ['dashboard', 'inventory', 'services'],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      inventory: { canAdd: false, viewOnly: true, hidePrices: true },
      'services-hub': { canAdd: true },
    },
  },
  'Service Manager': {
    visibleMenuIds: ['dashboard', 'inventory', 'services'],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      inventory: { canAdd: false, viewOnly: true },
      'services-hub': { canAdd: true },
    },
  },
  'Service Technician': {
    visibleMenuIds: ['dashboard', 'services'],
    canAdd: false,
    canEdit: false,
    canDelete: false,
    overrides: {
      'services-hub': { canAdd: true, ticketsOnly: true },
    },
  },
};

// Default for unknown roles – read-only everything
const DEFAULT_PERMS = {
  visibleMenuIds: ['dashboard'],
  canAdd: false,
  canEdit: false,
  canDelete: false,
  overrides: {},
};

export function getUserRole() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return '';
    const user = JSON.parse(raw);
    return user.role || '';
  } catch { return ''; }
}

export function getPermissions(role) {
  if (!role) role = getUserRole();
  return ROLE_PERMISSIONS[role] || DEFAULT_PERMS;
}

/** Check if a menu section id is visible for the current role */
export function isMenuVisible(sectionId, role) {
  const perms = getPermissions(role);
  if (perms.visibleMenuIds === '*') return true;
  return perms.visibleMenuIds.includes(sectionId);
}

/** Get action permissions for a specific section */
export function getSectionPerms(sectionId, role) {
  const perms = getPermissions(role);
  const ov = perms.overrides[sectionId] || {};
  return {
    canAdd: ov.canAdd !== undefined ? ov.canAdd : perms.canAdd,
    canEdit: ov.canEdit !== undefined ? ov.canEdit : perms.canEdit,
    canDelete: ov.canDelete !== undefined ? ov.canDelete : perms.canDelete,
    viewOnly: ov.viewOnly || false,
    hidePrices: ov.hidePrices || false,
    ticketsOnly: ov.ticketsOnly || false,
  };
}

/**
 * Map URL path segments to section IDs for auto-detection.
 */
const PATH_TO_SECTION = {
  'master-data': 'master-data',
  companies: 'master-data', branches: 'master-data', brands: 'master-data',
  categories: 'master-data', 'sub-categories': 'master-data', components: 'master-data',
  products: 'master-data', racks: 'master-data', specs: 'master-data',
  taxes: 'master-data', units: 'master-data', vendors: 'master-data',
  customers: 'master-data', 'payment-terms': 'master-data', 'payment-modes': 'master-data',
  purchase: 'purchase', 'purchase-bills': 'purchase', 'purchase-orders': 'purchase',
  'debit-notes': 'purchase', 'payment-out': 'purchase',
  sale: 'sale', invoices: 'sale', estimates: 'sale', proforma: 'sale',
  'credit-notes': 'sale', 'delivery-challans': 'sale', 'payment-in': 'sale',
  inward: 'inward', inventory: 'inventory',
  'part-sale': 'part-sale', 'part-sale-tracker': 'part-sale',
  services: 'services-hub', leads: 'services-hub', tickets: 'services-hub',
  tasks: 'services-hub', 'service-items': 'services-hub', receivables: 'services-hub',
  transactions: 'transactions',
  'org-data': 'org-data', users: 'org-data',
  'config-settings': 'config-settings',
};

/** Auto-detect section from current window path and return permissions */
export function detectPerms() {
  const path = window.location.pathname;
  const segments = path.split('/').filter(Boolean);
  // Walk from the end to find the most specific match
  for (let i = segments.length - 1; i >= 0; i--) {
    const sectionId = PATH_TO_SECTION[segments[i]];
    if (sectionId) return getSectionPerms(sectionId);
  }
  return getSectionPerms('dashboard');
}
